'use client';

import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Building2, ChevronDown, ChevronUp, Maximize2, User, X } from 'lucide-react';
import type { CompanyNetworkResponse } from '@/lib/backend-api';
import { trackNetworkClick } from '@/lib/network-click';
import { cn } from '@/lib/utils';
import { useLocaleTranslation } from '@/hooks/use-locale-translation';
import { useCompanyNetwork } from '@/hooks/use-company-network';
import { useLocale } from '@/hooks/use-locale';
import { edgeLabelLines, fmtDateString, nodeSubline } from './labels';
import { layoutGraph, mergeSegments, type GraphLayout, type MeasureText, type Size } from './layout';
import {
  buildFullGraph,
  EDGE_KINDS,
  isFarProcura,
  visibleGraph,
  type EdgeKind,
  type GraphEdge,
  type GraphNode,
} from './model';
import { NetworkPanel } from './NetworkPanel';
import styles from './network.module.css';

/** Above this many nodes the view opens at two levels instead of all. */
const DEFAULT_DEPTH_THRESHOLD = 24;
const GROUP_PREVIEW = 6;
/**
 * On the page the drawing always fits its slot (no scrolling); below this
 * scale it gets hard to read, so the default level is the deepest one that
 * fits at it, and a smaller drawing offers Vollbild. Vollbild shrinks to this
 * at most and scrolls beyond.
 */
const MIN_SCALE = 0.72;
/** Vollbild never enlarges beyond the drawing's natural size. */
const MAX_FS_SCALE = 1;

/**
 * The company page's network view (design: design/v2/netzwerk.html): a layered
 * diagram with owners above the company and management, Prokura and holdings
 * below, square nodes, orthogonal lines whose weight is the ownership share
 * (roles dashed), filter chips per connection kind, a level switch and
 * fullscreen. Clicking a box opens its details panel (below the diagram on the
 * page, beside it in fullscreen). Pure DOM + SVG — layout.ts places everything
 * from measured node sizes. Replaces the XyFlow diagram on the company page.
 */
export function CompanyNetworkView({
  euId,
  initialData,
  reloadToken,
  focalSub,
  focalFacts,
  capturedAt,
}: {
  euId: string;
  initialData: CompanyNetworkResponse;
  /** Bumped when the backend has new network data (see useCompanyDetailData). */
  reloadToken: number;
  /** Second line of the focal box ("Kiel · HRB 12345 KI"). */
  focalSub: string | null;
  /** Facts for the panel when the focal company is selected. */
  focalFacts: [string, string][];
  capturedAt?: string | null;
}) {
  const { t } = useLocaleTranslation();
  const { locale } = useLocale();
  const [fullscreen, setFullscreen] = useState(false);
  // Fullscreen loads the bigger graph once, and keeps it after closing.
  const [large, setLarge] = useState(false);
  const data = useCompanyNetwork(euId, initialData, { large, reloadToken });
  const focal = data.eu_id;

  const { graph: full } = useMemo(() => buildFullGraph(data), [data]);
  const fullById = useMemo(() => new Map(full.nodes.map((n) => [n.id, n])), [full]);
  const hasFarProcura = useMemo(() => full.edges.some((e) => isFarProcura(e, focal)), [full, focal]);
  const [kinds, setKinds] = useState<Set<EdgeKind>>(() => new Set(EDGE_KINDS));
  // Level 2 leaves out Prokura of other companies; "Alle" shows everything.
  const [depth, setDepth] = useState<number | null>(() =>
    (full.nodes.length > DEFAULT_DEPTH_THRESHOLD && full.maxDist > 2) || hasFarProcura ? 2 : null,
  );
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [selected, setSelected] = useState<string>(focal);
  const [hover, setHover] = useState<string | null>(null);
  // The details panel opens on a click, in Vollbild only (beside the diagram).
  const [panelOpen, setPanelOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const visible = useMemo(
    () => visibleGraph(full, focal, { kinds, depth, expanded, farProcura: depth == null }),
    [full, focal, kinds, depth, expanded],
  );
  const labelFor = useCallback((e: GraphEdge) => edgeLabelLines(e, locale, t), [locale, t]);

  // ── measure the boxes, then lay out
  const nodeRefs = useRef(new Map<string, HTMLElement>());
  const [sizes, setSizes] = useState<Map<string, Size>>(() => new Map());
  const [fontsTick, setFontsTick] = useState(0);
  // Boxes are measured once and then pinned to that width. A web font that
  // arrives later makes text wider, so it wraps and the box grows past what
  // the layout assumed (labels end up under boxes until the next re-measure).
  // On every font load: forget all sizes and measure again at natural width.
  useEffect(() => {
    let live = true;
    const remeasure = () => {
      if (!live) return;
      setSizes(new Map());
      setFontsTick((n) => n + 1);
    };
    document.fonts?.ready.then(remeasure);
    document.fonts?.addEventListener?.('loadingdone', remeasure);
    return () => {
      live = false;
      document.fonts?.removeEventListener?.('loadingdone', remeasure);
    };
  }, []);
  // And whatever else changes a box's height (wrapping, zoom): follow it.
  const sizeObserver = useRef<ResizeObserver | null>(null);
  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      setSizes((prev) => {
        let next: Map<string, Size> | null = null;
        for (const entry of entries) {
          const el = entry.target as HTMLElement;
          const id = el.dataset.nodeId;
          const known = id ? prev.get(id) : undefined;
          if (!id || !known || el.classList.contains(styles.measuring)) continue;
          const h = Math.ceil(el.offsetHeight);
          if (Math.abs(h - known.h) < 1) continue;
          next ??= new Map(prev);
          next.set(id, { ...known, h });
        }
        return next ?? prev;
      });
    });
    sizeObserver.current = ro;
    for (const el of nodeRefs.current.values()) ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useLayoutEffect(() => {
    let changed = false;
    const next = new Map(sizes);
    for (const n of visible.nodes) {
      const el = nodeRefs.current.get(n.id);
      if (!el) continue;
      const s = { w: Math.ceil(el.offsetWidth), h: Math.ceil(el.offsetHeight) };
      const prev = next.get(n.id);
      if (!prev || prev.w !== s.w || prev.h !== s.h) {
        next.set(n.id, s);
        changed = true;
      }
    }
    if (changed) setSizes(next);
    // sizes is the output here; re-measuring on its change would loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, fontsTick, locale]);
  // centre the drawing when it is narrower than the stage; on phones scroll
  // the focal into view instead
  const wrapRef = useRef<HTMLDivElement>(null);
  const [wrapW, setWrapW] = useState(0);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWrapW(el.clientWidth));
    ro.observe(el);
    setWrapW(el.clientWidth);
    return () => ro.disconnect();
  }, [collapsed]); // the stage remounts after a collapse
  // Label widths measured in the page font (estimates let labels overlap);
  // re-created when fonts finish loading.
  const measureText = useMemo<MeasureText | undefined>(() => {
    if (typeof document === 'undefined') return undefined;
    const ctx = document.createElement('canvas').getContext('2d');
    const el = wrapRef.current ?? document.body;
    if (!ctx) return undefined;
    const family = getComputedStyle(el).fontFamily;
    return (line, first) => {
      ctx.font = first ? `600 11.5px ${family}` : `500 11px ${family}`;
      return ctx.measureText(line).width;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fontsTick, sizes]);
  // Vollbild: the height left for the drawing below the controls
  const [fsAvailH, setFsAvailH] = useState(0);
  useEffect(() => {
    if (!fullscreen) return;
    const measure = () => {
      const el = wrapRef.current;
      if (el) setFsAvailH(window.innerHeight - el.getBoundingClientRect().top - 72);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [fullscreen, panelOpen]);
  const layout: GraphLayout | null = useMemo(() => {
    if (!visible.nodes.every((n) => sizes.has(n.id))) return null;
    // wrap rows wider than the slot can hold at MIN_SCALE (fullscreen: its width)
    const maxWidth = wrapW ? wrapW / (fullscreen ? 1 : MIN_SCALE) : undefined;
    return layoutGraph(visible.nodes, visible.edges, sizes, focal, labelFor, maxWidth, measureText);
  }, [visible, sizes, focal, labelFor, wrapW, fullscreen, measureText]);

  const fit = layout && wrapW ? Math.min(1, wrapW / layout.width) : 1;
  // Vollbild: fill the free width and height (legend and source line below)
  const fsFit = layout && wrapW && fsAvailH > 0 ? Math.min(MAX_FS_SCALE, wrapW / layout.width, fsAvailH / layout.height) : 1;
  const scale = fullscreen ? Math.max(MIN_SCALE, fsFit) : fit;
  const tooSmall = !fullscreen && fit < MIN_SCALE;
  const offsetX = layout ? Math.max(0, (wrapW - layout.width * scale) / 2) : 0;

  // Open at the deepest level that fits (at MIN_SCALE): step "Alle" → 2 → 1
  // until it does. Stops once the user picks a level.
  const autoDepth = useRef(true);
  useEffect(() => {
    if (!autoDepth.current || !layout || !wrapW) return;
    if (layout.width * MIN_SCALE <= wrapW || depth === 1) {
      autoDepth.current = false;
      return;
    }
    setDepth(depth == null ? 2 : depth - 1);
  }, [layout, wrapW, depth]);

  // Wider than the stage: fade the clipped side(s), and let a mouse drag the
  // drawing sideways (touch scrolls natively).
  const [clip, setClip] = useState({ left: false, right: false, down: false });
  const updateClip = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const left = el.scrollLeft > 2;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    const down = el.scrollTop + el.clientHeight < el.scrollHeight - 2;
    setClip((c) => (c.left === left && c.right === right && c.down === down ? c : { left, right, down }));
  }, []);
  useEffect(updateClip, [layout, wrapW, updateClip]);
  const drag = useRef<{ x: number; y: number; left: number; top: number; moved: boolean } | null>(null);
  const onPointerDown = (e: ReactPointerEvent) => {
    const el = wrapRef.current;
    const scrollable = fullscreen && !!el && (el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight);
    if (e.pointerType !== 'mouse' || e.button !== 0 || !el || !scrollable) return;
    if ((e.target as HTMLElement).closest('button, a')) return;
    drag.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop, moved: false };
    el.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: ReactPointerEvent) => {
    const el = wrapRef.current;
    const d = drag.current;
    if (!el || !d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (Math.abs(dx) + Math.abs(dy) > 3) d.moved = true;
    el.scrollLeft = d.left - dx;
    el.scrollTop = d.top - dy;
  };
  const onPointerUp = (e: ReactPointerEvent) => {
    if (!drag.current) return;
    drag.current = null;
    wrapRef.current?.releasePointerCapture(e.pointerId);
  };
  // First layout: centre the focal. Later ones (filters, levels, an expanded
  // group): keep the selected box — or the focal — in view.
  const centred = useRef(false);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !layout || !wrapW) return;
    const groupId = visible.nodes.find((n) => n.type === 'group' && n.members?.some((m) => m.id === selected))?.id;
    const b = layout.boxes.get(selected) ?? (groupId && layout.boxes.get(groupId)) ?? layout.boxes.get(focal);
    if (!b) return;
    const centre = offsetX + (b.x + b.w / 2) * scale;
    const inView = centre > el.scrollLeft + 40 && centre < el.scrollLeft + el.clientWidth - 40;
    if (centred.current && inView) return;
    centred.current = true;
    el.scrollLeft = Math.max(0, centre - el.clientWidth / 2);
    // selection changes alone must not scroll — the user just clicked in view
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, focal, offsetX, scale, wrapW]);

  // ── fullscreen: fixed overlay, body scroll locked, Escape closes
  const setFs = useCallback(
    (open: boolean, source?: 'fullscreen_button' | 'inline_overlay') => {
      trackNetworkClick(euId, {
        subEvent: open ? 'fullscreen_open' : 'fullscreen_close',
        source: open ? source : undefined,
      });
      setFullscreen(open);
      if (open) setLarge(true);
      else {
        // details live in Vollbild only; leaving it clears the selection
        setPanelOpen(false);
        setSelected(focal);
      }
    },
    [euId, focal],
  );
  const toggleFullscreen = useCallback(() => setFs(!fullscreen, 'fullscreen_button'), [setFs, fullscreen]);
  useEffect(() => {
    if (!fullscreen) return;
    // lock <html> too: a page-level scrollbar would shorten the fixed overlay
    const html = document.documentElement;
    const prev = [document.body.style.overflow, html.style.overflow];
    document.body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && toggleFullscreen();
    window.addEventListener('keydown', onKey);
    return () => {
      [document.body.style.overflow, html.style.overflow] = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [fullscreen, toggleFullscreen]);

  const toggleKind = (k: EdgeKind) => {
    const on = !kinds.has(k);
    if (!on && kinds.size === 1) return; // keep at least one kind
    const next = new Set(kinds);
    if (on) next.add(k);
    else next.delete(k);
    setKinds(next);
    trackNetworkClick(euId, { subEvent: 'filter_toggle', role: k, checked: on, source: 'filter_panel' });
  };

  // ── hover / focus highlight: the node, its neighbours and their edges
  const lit = useMemo(() => {
    // a hovered box can vanish under the pointer (expanded group, filter)
    if (!hover || !visible.nodes.some((n) => n.id === hover)) return null;
    const nodes = new Set([hover]);
    const edges = new Set<string>();
    for (const e of visible.edges) {
      if (e.up === hover || e.down === hover) {
        nodes.add(e.up);
        nodes.add(e.down);
        edges.add(e.id);
      }
    }
    return { nodes, edges };
  }, [hover, visible]);

  const litSegments = useMemo(
    () => (lit && layout ? mergeSegments(layout.paths.filter((p) => lit.edges.has(p.id))) : null),
    [lit, layout],
  );

  const selectedNode =
    visible.nodes.find((n) => n.id === selected) ?? fullById.get(selected) ?? fullById.get(focal)!;
  // Details are shown in Vollbild only: a click on the page opens it there.
  const select = (id: string) => {
    setSelected(id);
    setPanelOpen(true);
    if (!fullscreen) setFs(true, 'inline_overlay');
  };
  const closePanel = () => {
    setPanelOpen(false);
    setSelected(focal);
  };
  const showPanel = fullscreen && panelOpen;
  const groupOf = (memberId: string) =>
    visible.nodes.find((n) => n.type === 'group' && n.members?.some((m) => m.id === memberId))?.id;
  const isSel = (n: GraphNode) => n.id === selected || (n.type === 'group' && groupOf(selected) === n.id);

  const depthOptions: (number | null)[] =
    full.maxDist >= 3 || (full.maxDist === 2 && hasFarProcura)
      ? [1, 2, null]
      : full.maxDist === 2
        ? [1, null]
        : [];
  const kindLabel: Record<EdgeKind, string> = {
    own: t('companyNetwork.kinds.own'),
    manage: t('companyNetwork.kinds.manage'),
    proc: t('companyNetwork.kinds.proc'),
    sub: t('companyNetwork.kinds.sub'),
  };

  // Two people with one name (different birth dates in the register): tell them apart.
  const dupNames = useMemo(() => {
    const seen = new Set<string>();
    const dup = new Set<string>();
    for (const n of visible.nodes) {
      if (n.type !== 'person') continue;
      if (seen.has(n.name)) dup.add(n.name);
      seen.add(n.name);
    }
    return dup;
  }, [visible.nodes]);

  const nodeEl = (n: GraphNode) => {
    const b = layout?.boxes.get(n.id);
    const style = b
      ? { left: b.x, top: b.y, width: b.w, animationDelay: `${Math.abs(n.row) * 90}ms` }
      : undefined;
    const cls = cn(
      styles.node,
      n.type === 'center' && styles.center,
      n.type === 'person' && styles.person,
      n.type === 'group' && styles.group,
      isSel(n) && styles.sel,
      lit && (lit.nodes.has(n.id) ? styles.lit : styles.dim),
      !b && styles.measuring,
    );
    const setRef = (el: HTMLElement | null) => {
      const prev = nodeRefs.current.get(n.id);
      if (prev && prev !== el) sizeObserver.current?.unobserve(prev);
      if (el) {
        el.dataset.nodeId = n.id;
        nodeRefs.current.set(n.id, el);
        sizeObserver.current?.observe(el);
      } else nodeRefs.current.delete(n.id);
    };
    const hoverProps = {
      onPointerEnter: () => setHover(n.id),
      onPointerLeave: () => setHover((h) => (h === n.id ? null : h)),
      onFocus: () => setHover(n.id),
      onBlur: () => setHover((h) => (h === n.id ? null : h)),
    };
    if (n.type === 'group') {
      const members = n.members ?? [];
      const more = members.length - GROUP_PREVIEW;
      return (
        <div key={n.id} ref={setRef} className={cls} style={style} role="group" {...hoverProps}>
          <button type="button" className={styles.groupHead} onClick={() => select(n.id)}>
            {members[0]?.type === 'person'
              ? t('companyNetwork.group.persons', { count: members.length })
              : t('companyNetwork.group.companies', { count: members.length })}
          </button>
          <ul>
            {members.slice(0, GROUP_PREVIEW).map((m) => (
              <li key={m.id}>
                <button type="button" onClick={() => select(m.id)} aria-pressed={m.id === selected}>
                  {m.name}
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className={styles.groupMore}
            onClick={() => setExpanded((s) => new Set(s).add(n.id))}
          >
            {more > 0
              ? t('companyNetwork.group.more', { count: more })
              : t('companyNetwork.group.expand')}
          </button>
        </div>
      );
    }
    const year = n.type === 'person' && dupNames.has(n.name) ? n.raw?.date_of_birth?.slice(0, 4) : undefined;
    const sub =
      n.type === 'center'
        ? focalSub
        : [nodeSubline(n.raw), year && t('companyNetwork.bornYear', { year })].filter(Boolean).join(' · ') || null;
    return (
      <button
        key={n.id}
        ref={setRef}
        type="button"
        className={cls}
        style={style}
        aria-pressed={n.id === selected}
        onClick={() => select(n.id)}
        {...hoverProps}
      >
        <span className={styles.ic} aria-hidden="true">
          {n.type === 'person' ? <User size={13} /> : <Building2 size={n.type === 'center' ? 15 : 13} />}
        </span>
        <span>
          <span className={styles.nm}>{n.name}</span>
          {sub && <span className={styles.sb}>{sub}</span>}
        </span>
      </button>
    );
  };

  return (
    <section className={cn(styles.net, fullscreen && styles.fs)} aria-label={t('network.title', 'Netzwerk')}>
      <div className={styles.head}>
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
          {t('network.title', 'Netzwerk')}
        </h2>
        {!fullscreen && (
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label={collapsed ? t('companyNetwork.expand') : t('companyNetwork.collapse')}
            aria-expanded={!collapsed}
          >
            {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        )}
      </div>
      {!(collapsed && !fullscreen) && (
      <>
      <div className={styles.controls}>
        {full.kinds.length > 1 && (
          <div className={styles.group_} role="group" aria-label={t('companyNetwork.filters')}>
            {full.kinds.map((k) => (
              <button
                key={k}
                type="button"
                className={styles.chip}
                aria-pressed={kinds.has(k)}
                onClick={() => toggleKind(k)}
              >
                {kindLabel[k]}
              </button>
            ))}
          </div>
        )}
        {depthOptions.length > 0 && (
          <div className={styles.group_} role="group" aria-label={t('companyNetwork.depth')}>
            <span>{t('companyNetwork.depth')}</span>
            {depthOptions.map((d) => (
              <button
                key={d ?? 'all'}
                type="button"
                className={styles.chip}
                aria-pressed={depth === d}
                onClick={() => {
                  autoDepth.current = false;
                  setDepth(d);
                }}
              >
                {d ?? (depthOptions.length === 2 ? '2' : t('companyNetwork.depthAll'))}
              </button>
            ))}
          </div>
        )}
        {/* too big to read at page size: point at Vollbild instead of an extra button */}
        {tooSmall && (
          <span className={styles.pillHint} aria-hidden="true">
            {t('companyNetwork.tooBig')} →
          </span>
        )}
        <button
          type="button"
          className={cn(styles.pill, tooSmall && styles.pillCalling)}
          aria-pressed={fullscreen}
          aria-describedby={tooSmall ? 'fd-net-toobig' : undefined}
          onClick={toggleFullscreen}
        >
          {fullscreen ? <X size={14} aria-hidden="true" /> : <Maximize2 size={13} aria-hidden="true" />}
          {fullscreen ? t('companyNetwork.close') : t('companyNetwork.fullscreen')}
          {tooSmall && (
            <span id="fd-net-toobig" className="sr-only">
              {t('companyNetwork.tooBig')}
            </span>
          )}
        </button>
      </div>

      <div className={cn(styles.body, showPanel && styles.withPanel)}>
        <div className={styles.main}>
          <div
            data-nosnippet
            className={cn(
              styles.stageWrap,
              fullscreen && styles.scrolls,
              clip.down && styles.clipB,
              clip.left && styles.clipL,
              clip.right && styles.clipR,
              (clip.left || clip.right || clip.down) && styles.pannable,
            )}
            ref={wrapRef}
            onScroll={updateClip}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <div
              className={styles.sizer}
              style={layout ? { width: layout.width * scale, height: layout.height * scale, marginLeft: offsetX } : { height: 240 }}
            >
            <div
              className={styles.stage}
              style={layout ? { width: layout.width, height: layout.height, transform: scale !== 1 ? `scale(${scale})` : undefined } : undefined}
              aria-label={t('companyNetwork.stageLabel')}
              onPointerLeave={() => setHover(null)}
            >
              {layout && (
                <svg className={styles.edges} width={layout.width} height={layout.height} aria-hidden="true">
                  <g>
                    {/* base: every stretch once, so shared stretches look like single ones */}
                    {layout.segments.map((sg, i) => (
                      <path key={i} d={sg.d} className={cn(styles.edge, sg.dashed && styles.role, lit && styles.dim)} strokeWidth={1.4} />
                    ))}
                    {/* highlighted links on top, merged the same way (shared dashed
                        stretches drawn twice out of phase would look solid) */}
                    {litSegments?.map((sg, i) => (
                      <path key={`lit${i}`} d={sg.d} className={cn(styles.edge, sg.dashed && styles.role, styles.lit)} strokeWidth={1.4} />
                    ))}
                  </g>
                </svg>
              )}
              {layout?.labels.map((l) => (
                <div
                  key={l.id}
                  className={cn(
                    styles.elabel,
                    l.anchor === 'side' && styles.side,
                    lit && (l.edgeIds.some((id) => lit.edges.has(id)) ? styles.lit : styles.dim),
                    l.hidden && !(lit && l.edgeIds.some((id) => lit.edges.has(id))) && styles.crowded,
                  )}
                  style={{ left: l.x, top: l.y }}
                  data-edges={l.edgeIds.join(' ')}
                >
                  {l.text.split('\n').map((line, i) => (i ? <small key={i}>{line}</small> : <span key={i}>{line}</span>))}
                </div>
              ))}
              {visible.nodes.map(nodeEl)}
            </div>
            </div>
          </div>
          <div className={styles.legend}>
            <span>
              <b />
              {t('companyNetwork.legend.company')}
            </span>
            <span>
              <b className={styles.round} />
              {t('companyNetwork.legend.person')}
            </span>
            <span>
              <i />
              {t('companyNetwork.legend.capital')}
            </span>
            <span>
              <i className={styles.dash} />
              {t('companyNetwork.legend.role')}
            </span>
          </div>
        </div>
        {showPanel && (
        <aside className={styles.panel}>
          <button type="button" className={styles.panelClose} onClick={closePanel} aria-label={t('companyNetwork.panel.close')}>
            <X size={16} />
          </button>
          <NetworkPanel
            key={selectedNode.id}
            node={selectedNode}
            allEdges={full.edges}
            nodeById={fullById}
            focalFacts={focalFacts}
            locale={locale}
            t={t}
            onSelect={select}
          />
        </aside>
        )}
      </div>
      {/* on the page the section's own source line follows; fullscreen needs one */}
      {fullscreen && capturedAt && (
        <div className={styles.stand}>
          {t('companyNetwork.stand', { date: fmtDateString(capturedAt.slice(0, 10), locale) })}
        </div>
      )}
      </>
      )}
    </section>
  );
}
